import { Injectable } from '@angular/core';
import { ChartDateIntervalSelectorMapperService } from '@core/components/charts/chart-date-interval-selector/services/chart-date-interval-selector.mapper.service';
import { MaintenanceChartDataItem, VolumetryFilter } from '@core/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { chain } from 'lodash';
import moment from 'moment';
import { LineMapperService } from '../../../../core/components/charts/line/services/line.mapper.service';

@Injectable({
  providedIn: 'root'
})
export class VolumetryService {

  constructor(
    private readonly translate: TranslateService,
    private readonly apollo: Apollo,
    private readonly mapper: LineMapperService
  ) {}

  private positionByPeriod(period: string): number {
    const map = {
      'up_to_2_days': 0,
      'between_3_and_6_days': 1,
      'between_7_and_15_days': 2,
      'more_than_15_days': 3,
    };

    return map[period];
  }

  async accountMaintenancesSegments(where: VolumetryFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesSegments ($where: DashboardInput!) {
        accountMaintenancesSegments(where: $where, limit: 10) {
          reportName
          items {
            name: key
            value
            extra
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        fetchPolicy: 'cache-first',
        query,
        variables: { where },
      })
      .toPromise()
      .then(({ data }) => {
        return chain(data.accountMaintenancesSegments.items)
          .orderBy('value')
          .reverse()
          .value();
      });
  }

  async accountMaintenancesTypes(where: VolumetryFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesTypes ($where: DashboardInput!) {
        accountMaintenancesTypes(where: $where, limit: 10) {
          reportName
          items {
            name: key
            value
            extra
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        fetchPolicy: 'cache-first',
        query,
        variables: { where },
      })
      .toPromise()
      .then(({ data }) => {
        return chain(data.accountMaintenancesTypes.items)
          .orderBy('value')
          .reverse()
          .value();
      });
  }

  private autofillChartDataGaps(inputChartData: any): MaintenanceChartDataItem[] {
    const momentsInterval = [
      'up_to_2_days',
      'between_3_and_6_days',
      'between_7_and_15_days',
      'more_than_15_days'
    ];
    const chartDataInput = inputChartData.accountMaintenancesPendingTimeInterval.items;

    if (chartDataInput.length < 4) {
      for (let index = 0; index < 4; index++) {
        chartDataInput.push({ name: momentsInterval[index], value: 0, extra: null });
      }
    }

    return chartDataInput;
  }

  async accountMaintenancesPendingTimeInterval(where: VolumetryFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesPendingTimeInterval ($where: DashboardInput!) {
        accountMaintenancesPendingTimeInterval(where: $where) {
          reportName
          items {
            name: key
            value
            extra
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        fetchPolicy: 'cache-first',
        query,
        variables: { where },
      })
      .toPromise()
      .then(({ data }) => {
        const processedChartData = this.autofillChartDataGaps(data);

        return chain(processedChartData)
          .cloneDeep()
          .orderBy((item) => this.positionByPeriod(item.name))
          .map((value) => {
            const key = `VOLUMETRY.CHART.OPEN_MAINTENANCE_PER_PERIOD.${value.name}`.toUpperCase();
            value.name = this.translate.instant(key);
            return value;
          })
          .value();
      });
  }

  async accountMaintenancesTopPendingTime(where: VolumetryFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesTopPendingTime ($where: DashboardInput!) {
        accountMaintenancesTopPendingTime(where: $where, limit: 10) {
          reportName
          items {
            name: key
            value
            extra
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        fetchPolicy: 'cache-first',
        query,
        variables: { where },
      })
      .toPromise()
      .then(({ data }) => {
        return chain(data.accountMaintenancesTopPendingTime.items)
          .cloneDeep()
          .orderBy('value')
          .reverse()
          .map((item) => {
            item.extra.location = { name: item.name };
            item.name = `${item.name} • ${item.extra.maintenance.number}`;
            return item;
          })
          .value();
      });
  }

  async accountMaintenancesCompletedBySources(where: VolumetryFilter): Promise<any> {
    const query = gql`
        query accountMaintenancesCompletedBySources($where: DashboardInput!) {
          accountMaintenancesCompletedBySources(where: $where) {
            reportName
            items {
              name: key
              value
            }
          }
        }
    `;

    return this.apollo
      .query<any>({
        fetchPolicy: 'cache-first',
        query,
        variables: { where },
      })
      .toPromise()
      .then(({ data }) => {
        const map = {
          'orc': 'App do colaborador',
          'gothmog': 'App do gestor',
          'mordor': 'Painel do gestor',
          'carchost': 'API'
        };

        return chain(data.accountMaintenancesCompletedBySources.items)
          .cloneDeep()
          .map((item) => {
            item.name = map[item.name] || item.name;
            return item;
          })
          .value();
      });
  }

  async accountMaintenancesCompletedTimeline(interval, where: VolumetryFilter): Promise<any> {
    delete where.from;
    delete where.to;
    const query = gql`
      query accountMaintenancesCompletedTimeline($where: DashboardMaintenancesTimelineWhereInput!) {
        accountMaintenancesCompletedTimeline(where: $where) {
          reportName
          items {
            closedAt
            totalCount
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        fetchPolicy: 'cache-first',
        query,
        variables: {
          where: {
            ...where,
            interval: interval
          }
        },
      })
      .toPromise()
      .then(({ data }) => {
        const mappedData = chain(data.accountMaintenancesCompletedTimeline.items)
          .cloneDeep()
          .map((item) => {
            item['completedAt'] = item['closedAt'];
            delete item.closedAt;
            return item;
          })
          .value();
        return this.mapper.toLineChartFormat(mappedData, interval);
      });
  }

  async accountMaintenancesCompletedLineChart(where: VolumetryFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesCompletedLineChart($where: DashboardMaintenancesLineChartWhereInput!) {
        accountMaintenancesCompletedLineChart(where: $where) {
          reportName
          items {
            date: closedAt
            totalCount
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        fetchPolicy: 'cache-first',
        query,
        variables: {
          where: {
            ...where,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        },
      })
      .toPromise()
      .then(({ data }) => {
        return data.accountMaintenancesCompletedLineChart;
      });
  }
}
